import * as React from "react";
import { useTranslation } from "react-i18next";
import { TextField } from "./ui/TextField";
import { Button } from "./ui/Button";
import { FormError } from "./ui/FormError";
import { Icon } from "./ui/Icon";
import { useContent, text } from "../hooks/useContent";
import { useSubscribeMutation } from "../hooks/useSubscribe";

const isValidEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value.trim());

type Props = {
  /**
   * Same SPA-navigation escape hatch as Footer: the consent privacy link
   * can't use react-router's <Link> because the home-scene footer renders in
   * drei's detached <Scroll html> root, where the router context doesn't
   * reach. Falls back to a full page load when the prop is absent.
   */
  onLegalNavigate?: (path: string) => void;
};

/** Footer email-capture band: a short pitch for the free Heaven's Bridge guide
 *  plus an inline form that reuses the /subscribe endpoint. */
export function FooterSubscribe({ onLegalNavigate }: Props = {}) {
  const { data: content } = useContent();
  const { t } = useTranslation("subscribe");
  const mutation = useSubscribeMutation("footer");
  const [email, setEmail] = React.useState("");
  const [showErrors, setShowErrors] = React.useState(false);

  const emailError = isValidEmail(email) ? undefined : t("emailInvalid");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mutation.isPending) return;
    if (emailError) {
      setShowErrors(true);
      return;
    }
    mutation.mutate(email.trim());
  };

  const handlePrivacyClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!onLegalNavigate) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    onLegalNavigate("/privacy-policy");
  };

  return (
    <div>
      {/* Column heading — matches the sibling footer column headings. */}
      <h4 className="t-label-heavy m-0">
        {text(content, "footer.subscribe.heading")}
      </h4>
      <p className="mt-4 text-[13px] leading-relaxed text-white/85">
        {text(content, "footer.subscribe.blurb")}
      </p>

      {/* Stacked form in the footer column: full-width white input, full-width
          white outline submit below, light-on-blue copy. */}
      {mutation.isSuccess ? (
        <div className="mt-5 flex items-center gap-3">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-white/10 text-[18px] text-white">
            <Icon name="verified" title={text(content, "subscribe.success.title")} />
          </span>
          <p className="t-label-heavy m-0 text-white">
            {text(content, "subscribe.success.title")}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-3" noValidate>
          {/* Label is visually hidden — the placeholder carries the meaning. */}
          <TextField
            label={t("emailLabel")}
            hideLabel
            requirement="required"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder={t("emailPlaceholder")}
            name="email"
            autoComplete="email"
            disabled={mutation.isPending}
            error={showErrors ? emailError : undefined}
          />
          {mutation.isError && <FormError message={t("sendError")} surface="dark" />}
          <Button
            variant="outline"
            fluid
            as="button"
            type="submit"
            disabled={mutation.isPending}
          >
            {t("submit")}
          </Button>
          <p className="text-xs leading-snug text-white/70">
            {t("consentNote")}{" "}
            <a
              href="/privacy-policy"
              onClick={handlePrivacyClick}
              className="underline underline-offset-2 hover:text-white"
            >
              {t("privacyLinkLabel")}
            </a>
          </p>
        </form>
      )}
    </div>
  );
}
